import type { EmailOtpType } from '@supabase/supabase-js';
import { supabaseClient } from '../services/supabase';
import { setPasswordRecoveryPending } from './passwordRecoveryPending';

const EMAIL_CONFIRMATION_TYPES = new Set(['signup', 'email', 'email_change', 'invite']);

export function getSearchParams(): URLSearchParams {
    const query = new URLSearchParams(window.location.search);
    const hash = window.location.hash.startsWith('#')
        ? new URLSearchParams(window.location.hash.slice(1))
        : new URLSearchParams();
    const merged = new URLSearchParams(query);
    hash.forEach((value, key) => {
        if (!merged.has(key)) merged.set(key, value);
    });
    return merged;
}

function hasAuthTokens(params: URLSearchParams): boolean {
    return params.has('token_hash') || params.has('code') || params.has('access_token');
}

function isResetPasswordPath(pathname = window.location.pathname): boolean {
    return pathname === '/reset-password' || pathname === '/password-reset';
}

/** True when the URL is a Supabase email-confirmation (signup) callback. */
export function hasEmailConfirmationCallback(params = getSearchParams()): boolean {
    const type = params.get('type');
    if (type && EMAIL_CONFIRMATION_TYPES.has(type)) return true;

    if (!hasAuthTokens(params)) return false;

    const pathname = window.location.pathname;
    if (pathname === '/login' || pathname === '/') return true;

    return false;
}

/** True only for password-recovery callbacks — not signup/email confirmation. */
export function hasRecoveryCallbackParams(params = getSearchParams()): boolean {
    const type = params.get('type');
    if (type === 'recovery') return true;
    if (type && EMAIL_CONFIRMATION_TYPES.has(type)) return false;
    if (type) return false;

    if (!hasAuthTokens(params)) return false;

    return isResetPasswordPath();
}

/** Supabase auth error redirect (e.g. otp_expired). */
export function getAuthCallbackError(params = getSearchParams()): string | null {
    const error = params.get('error');
    const errorCode = params.get('error_code');
    if (!error && !errorCode) return null;

    const isRecovery = hasRecoveryCallbackParams(params);

    if (errorCode === 'otp_expired') {
        return isRecovery
            ? 'This password reset link has expired. Request a new link from the sign-in page.'
            : 'This confirmation link has expired. Request a new confirmation email from the sign-in page.';
    }

    const description = params.get('error_description');
    if (description) {
        return decodeURIComponent(description.replace(/\+/g, ' '));
    }

    return isRecovery
        ? 'This reset link is invalid. Request a new link from the sign-in page.'
        : 'This link is invalid. Try signing in or request a new confirmation email.';
}

export function stripAuthParamsFromUrl(): void {
    const url = new URL(window.location.href);
    [
        'token_hash',
        'type',
        'code',
        'access_token',
        'refresh_token',
        'expires_in',
        'error',
        'error_code',
        'error_description',
    ].forEach((key) => {
        url.searchParams.delete(key);
    });
    url.hash = '';
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
}

async function completeAuthCallbackFromUrl(
    options: { recovery: boolean }
): Promise<{ ok: boolean; error?: string }> {
    const params = getSearchParams();

    const callbackError = getAuthCallbackError(params);
    if (callbackError) {
        stripAuthParamsFromUrl();
        return { ok: false, error: callbackError };
    }

    const token_hash = params.get('token_hash');
    const type = params.get('type') as EmailOtpType | null;
    const code = params.get('code');

    if (options.recovery) {
        if (type && EMAIL_CONFIRMATION_TYPES.has(type)) {
            return {
                ok: false,
                error: 'This link confirms your email. Open it from the confirmation email, or sign in after confirming.',
            };
        }
    } else if (type === 'recovery') {
        return {
            ok: false,
            error: 'This link is for resetting your password. Use the link from your password reset email.',
        };
    }

    if (token_hash && type) {
        const { error } = await supabaseClient.auth.verifyOtp({ token_hash, type });
        if (error) return { ok: false, error: error.message };
        stripAuthParamsFromUrl();
        return { ok: true };
    }

    const { data: { session }, error: sessionError } = await supabaseClient.auth.getSession();
    if (sessionError) return { ok: false, error: sessionError.message };
    if (session) {
        stripAuthParamsFromUrl();
        return { ok: true };
    }

    if (code) {
        const { error } = await supabaseClient.auth.exchangeCodeForSession(code);
        if (error) {
            const verifierMissing = /code verifier/i.test(error.message);
            return {
                ok: false,
                error: verifierMissing
                    ? options.recovery
                        ? 'This reset link cannot be opened from email. Request a new reset email, then open the link in the same browser where you requested it—or after the app is updated, links will work from any browser.'
                        : 'This confirmation link cannot be opened from email. Open it in the same browser where you signed up, or sign in if you have already confirmed.'
                    : error.message,
            };
        }
        stripAuthParamsFromUrl();
        return { ok: true };
    }

    return {
        ok: false,
        error: options.recovery
            ? 'This reset link is invalid or has expired. Request a new link from the login page.'
            : 'This confirmation link is invalid or has expired.',
    };
}

/**
 * Establishes a session from a password-recovery link (type=recovery or tokens on /reset-password).
 */
export async function establishRecoverySessionFromUrl(): Promise<{ ok: boolean; error?: string }> {
    const params = getSearchParams();

    if (!hasRecoveryCallbackParams(params)) {
        return {
            ok: false,
            error: 'This reset link is invalid or has expired. Request a new link from the login page.',
        };
    }

    setPasswordRecoveryPending();
    return completeAuthCallbackFromUrl({ recovery: true });
}

/**
 * Establishes a session from a signup / email-confirmation link (redirected to /login).
 */
export async function establishEmailConfirmationSessionFromUrl(): Promise<{ ok: boolean; error?: string }> {
    const params = getSearchParams();

    if (!hasEmailConfirmationCallback(params)) {
        return { ok: false, error: 'Invalid confirmation link.' };
    }

    return completeAuthCallbackFromUrl({ recovery: false });
}
